/**
 * @module exportTableToExcel
 * Pure utility functions for table export functionality with intelligent data type handling
 * 
 * Handles all data transformation and Excel export logic including:
 * - Automatic data type detection (numbers, dates, booleans, strings)
 * - Timezone conversion from UTC to client timezone (America/Chicago)
 * - Complex field handling (ranges, GeoPoints, references)
 * - Dynamic column width calculation
 * - Configurable filename generation
 *
 * The column type gates the write here, nulling any value that disagrees with it.
 * That is this legacy embedded-`TableValue` path only, which carries no bundle
 * aggregations; the ArTable writers coerce per value through
 * `coerceArTableSheetCell` instead (SYD-2291), so do not read the rule below as
 * the current one.
 */

import { TableValue, TableColumnType, TableRow, ExportOptions, type FieldType } from '#types/System.types';
import type { StreetAddress, TelephoneNumber } from '#types/System.FieldTypes.types';
import { formatStreetAddressFromComponents, ADDRESS_COMPONENT_KEYS } from '#types/System.StreetAddress.types';
import { formatTelephoneFromComponents } from '@/components/rendering/fields/formatTelephoneFromComponents';
import { 
  EXPORT_COLUMN_MIN_WIDTH, 
  EXPORT_COLUMN_MAX_WIDTH,
  EXPORT_MAX_ROWS,
  EXPORT_FILENAME_RECIPE
} from '#types/System.Parameters.types';
import { defaultTimeZone } from '#types/Client.Parameters.types';
import { resolveTrackingUrlForShippingValue } from '@/components/rendering/fields/ShippingTrackerUI/resolveTrackingUrlForShippingValue';
import { isShippingTrackerFieldTypeId } from '@/components/rendering/fields/ShippingTrackerUI/isShippingTrackerFieldTypeId';
import { COMPOSITE_FIELD_EMPTY_PLACEHOLDER } from '@/components/CompositeField/compositeFieldPlaceholders';

const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const sanitizeExcelSheetName = (name: string): string => {
  const cleaned = name.replace(/[\\/?*[\]:]/g, ' ').trim();
  return cleaned.length > 31 ? cleaned.substring(0, 31) : cleaned;
};

/**
 * Generates a standardized filename for Excel exports using template substitution
 * 
 * @param options - Export options containing form context information
 * @param options.form - Form metadata including form number
 * @param options.formVersion - Version information with version number
 * @param options.formType - Form type with description and short label
 * @returns Sanitized filename with .xlsx extension following EXPORT_FILENAME_RECIPE pattern
 * 
 * @example
 * // With full context: "invoice-v2-sales-2024-01-15.xlsx"
 * // Without context: "export-2024-01-15.xlsx"
 */
const generateExportFilename = (options: ExportOptions): string => {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  
  // Default filename if no form context available
  if (!options.form || !options.formVersion || !options.formType) {
    const sanitize = (str: string) => str.replace(/[^a-zA-Z0-9-_]/g, '-');
    const label = options.filenameLabel?.trim();
    const safeLabel = label ? sanitize(label) : '';
    if (safeLabel) {
      return `export-${safeLabel}-${today}.xlsx`;
    }
    return `export-${today}.xlsx`;
  }
  
  const formNumber = options.form.formNumber || 'unknown';
  const version = `v${options.formVersion.formVersionNumber || '1'}`;
  const formType = options.formType.description?.shortLabel || options.formType.docId || 'unknown';
  
  // Sanitize filename parts (remove invalid filename characters)
  const sanitize = (str: string) => str.replace(/[^a-zA-Z0-9-_]/g, '-');
  
  // Replace placeholders in the filename recipe
  return EXPORT_FILENAME_RECIPE
    .replace('{formNumber}', sanitize(formNumber))
    .replace('{version}', sanitize(version))
    .replace('{formType}', sanitize(formType))
    .replace('{date}', today);
};

/**
 * Calculates the display length of any value for column width estimation
 * 
 * @param value - Any value from table data (primitive, Date, object, array)
 * @returns Character count of the formatted display value
 */
const getDisplayLength = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'boolean') return 3; // "Yes"/"No"
  if (value instanceof Date) return value.toLocaleDateString().length;
  if (Array.isArray(value)) return value.join(', ').length;
  if (typeof value === 'object') {
    // Handle Firestore timestamp objects (various formats)
    if ((value.type === 'firestore/timestamp' || value.type === 'firestore/timestamp/1.0') && typeof value.seconds === 'number') {
      const date = new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
      return date.toLocaleDateString().length;
    }
    // Handle other objects
    return (value.displayValue || value.label || value.formatted || String(value.entityRecordID || '')).length;
  }
  return String(value).length;
};

/**
 * Calculates optimal column width based on header and content analysis
 * 
 * @param column - Column definition with dataIndex and description
 * @param rows - Table rows to analyze for content width
 * @param cellType - Optional cell type hint for specialized formatting (e.g., Date)
 * @returns Column width in characters, constrained between MIN and MAX limits
 */
const getColumnWidth = (column: TableColumnType, rows: TableRow[], cellType?: any): number => {
  const headerLength = (column.description?.shortLabel || column.dataIndex).length;
  let maxLength = headerLength;
  
  // For Date types, use the formatted date length
  if (cellType === Date) {
    maxLength = Math.max(maxLength, 19); // "MM/DD/YYYY HH:mm:ss" = 19 characters
  } else {
    for (const row of rows) {
      const value = row?.value?.[column.dataIndex];
      const displayLength = getDisplayLength(value);
      maxLength = Math.max(maxLength, displayLength);
    }
  }
  
  return Math.min(EXPORT_COLUMN_MAX_WIDTH, Math.max(EXPORT_COLUMN_MIN_WIDTH, maxLength + 2));
};

/**
 * Converts a UTC Date to the client's configured timezone for export
 * 
 * @param utcDate - Date object in UTC time
 * @returns New Date object adjusted to client timezone (defaultTimeZone from config)
 * 
 * @remarks
 * Excel interprets dates as local time, so this conversion ensures timestamps
 * appear correctly in the user's expected timezone (typically America/Chicago)
 */
const convertToClientTimeZone = (utcDate: Date): Date => {
  // Create a new date that represents the same moment in time but will be displayed in the client timezone
  // Excel will interpret this as a local time in the user's system timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: defaultTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(utcDate);
  const year = parseInt(parts.find(p => p.type === 'year')?.value || '0');
  const month = parseInt(parts.find(p => p.type === 'month')?.value || '1') - 1; // Month is 0-indexed
  const day = parseInt(parts.find(p => p.type === 'day')?.value || '1');
  const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
  const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
  const second = parseInt(parts.find(p => p.type === 'second')?.value || '0');
  
  // Create a new Date object that Excel will treat as local time
  return new Date(year, month, day, hour, minute, second);
};

/**
 * Converts various timestamp formats to Date objects in client timezone
 * 
 * @param value - Timestamp in various formats (Date, Firestore timestamp, emulator format)
 * @returns Date object in client timezone or original value if not a timestamp
 * 
 * @remarks
 * Handles multiple timestamp formats:
 * - Native Date objects
 * - Firestore timestamps with seconds/nanoseconds
 * - Emulator timestamps with _type and value fields
 */
const convertTimestampToDate = (value: any): Date | any => {
  if (!value) return value;
  
  // Already a Date - convert to client timezone for display
  if (value instanceof Date) {
    return convertToClientTimeZone(value);
  }
  
  let utcDate: Date;
  
  // Handle Firestore timestamp objects - all possible formats
  if (value.type && value.type.includes('firestore/timestamp') && typeof value.seconds === 'number') {
    utcDate = new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
  }
  // Also check for plain timestamp objects with seconds property
  else if (typeof value.seconds === 'number' && value.nanoseconds !== undefined) {
    utcDate = new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
  }
  // Also support serialized FirestoreTimestamp shape used across client/server: {_seconds, _nanoseconds}
  else if (typeof value._seconds === 'number') {
    utcDate = new Date(value._seconds * 1000 + (value._nanoseconds || 0) / 1000000);
  }
  // Check for emulator timestamp format: {_type: "Timestamp", value: "ISO_STRING"}
  else if (value._type === 'Timestamp' && typeof value.value === 'string') {
    utcDate = new Date(value.value);
  }
  else {
    return value;
  }
  
  return convertToClientTimeZone(utcDate);
};

/**
 * Type guard to check if a value is a range object
 * 
 * @param value - Value to check
 * @returns True if value has both 'start' and 'end' properties
 */
const isRangeValue = (value: any): boolean => {
  return typeof value === 'object' && value !== null && 
         ('start' in value) && ('end' in value);
};

/**
 * Type guard to check if a value is a GeoPoint object
 * 
 * @param value - Value to check
 * @returns True if value has both 'latitude' and 'longitude' properties
 */
const isGeoPointValue = (value: any): boolean => {
  return typeof value === 'object' && value !== null && 
         ('latitude' in value) && ('longitude' in value);
};

/**
 * Type guard to check if a value is a Firestore Reference object
 * 
 * @param value - Value to check
 * @returns True if value has a 'path' property
 */
const isReferenceValue = (value: any): boolean => {
  return typeof value === 'object' && value !== null && 
         ('path' in value);
};

/**
 * Formats values for Excel cells with proper type preservation
 * 
 * @param value - Raw value from table data
 * @returns Formatted value suitable for Excel (preserves numbers, dates, booleans)
 * 
 * @remarks
 * Maintains native types where possible for Excel formatting:
 * - Numbers remain as numbers
 * - Booleans remain as booleans  
 * - Dates are converted to client timezone
 * - Arrays are joined with commas
 * - Complex objects are serialized appropriately
 */
const formatShippingTrackerForExport = (value: unknown): string => {
  const url = resolveTrackingUrlForShippingValue(value);
  const components = (value as { components?: { trackingNumber?: unknown } }).components;
  const trackingNumber =
    typeof components?.trackingNumber === 'string' ? components.trackingNumber.trim() : '';
  const formatted = (value as { formatted?: unknown }).formatted;
  const base =
    typeof formatted === 'string' && formatted.trim() && formatted.trim() !== COMPOSITE_FIELD_EMPTY_PLACEHOLDER
      ? formatted.trim()
      : trackingNumber;
  if (url) return base ? `${base}\n${url}` : url;
  if (typeof formatted === 'string' && formatted.trim() !== '') return formatted;
  return trackingNumber;
};

const formatCellValue = (value: any, fieldType?: FieldType): any => {
  // Handle null/undefined/empty
  if (value === null || value === undefined) return '';
  
  // Firestore native types are already in the correct format for Excel
  
  // Numbers - Firestore stores as actual numbers
  if (typeof value === 'number') return value;
  
  // Booleans - Firestore stores as actual booleans
  if (typeof value === 'boolean') return value;
  
  // Dates - Firestore returns as Date objects when properly configured
  if (value instanceof Date) return value;
  
  // Convert timestamps to dates
  const convertedValue = convertTimestampToDate(value);
  if (convertedValue instanceof Date) return convertedValue;
  
  if (Array.isArray(value)) {
    return value.map((item) => formatCellValue(item, fieldType)).join(', ');
  }
  
  // Handle objects
  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>;

    if (fieldType && isShippingTrackerFieldTypeId(fieldType.fieldDataTypeId)) {
      return formatShippingTrackerForExport(value);
    }

    if ('referenceType' in record && typeof record.docId === 'string') {
      const label = record.cacheLabel;
      return typeof label === 'string' && label.length > 0 ? label : record.docId;
    }

    if (record.valueType === 'manual') {
      const raw = record.value;
      if (raw == null) return '';
      if (typeof raw === 'string') return raw;
      if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
      return '';
    }

    if (record.kind === 'subField' && 'value' in record) {
      return formatCellValue(record.value);
    }

    // Composite values with components: prefer numeric price, else formatted string
    if ('components' in value) {
      const components = (value as any).components as Record<string, unknown> | undefined;
      const price = components?.price;
      if (typeof price === 'number' && Number.isFinite(price)) {
        return price;
      }
      const formatted = (value as any).formatted;
      if (typeof formatted === 'string' && formatted.trim().length > 0) {
        return formatted;
      }
    }

    if (
      'components' in record &&
      record.components &&
      typeof record.components === 'object' &&
      !Array.isArray(record.components)
    ) {
      const c = record.components as Record<string, unknown>;
      const hasAddressKeys = ADDRESS_COMPONENT_KEYS.some((key) => key in c);
      const hasPhoneKeys = 'countryCode' in c || 'number' in c || 'extension' in c;
      if (hasPhoneKeys && !hasAddressKeys) {
        const s = formatTelephoneFromComponents(
          c as NonNullable<TelephoneNumber['components']>,
        );
        if (s.length > 0) return s;
      }
      if (hasAddressKeys) {
        return formatStreetAddressFromComponents(
          c as NonNullable<StreetAddress['components']>,
        );
      }
    }

    // Composite values with fields: extract meaningful sub-field values (not AR references / manual; handled above)
    if (
      'fields' in value &&
      typeof value.fields === 'object' &&
      value.fields !== null &&
      !('referenceType' in record) &&
      record.valueType !== 'manual'
    ) {
      const fields = value.fields as Record<string, { value?: unknown; meta?: unknown }>;
      const parts: string[] = [];
      for (const [, subField] of Object.entries(fields)) {
        if (subField && typeof subField === 'object' && 'value' in subField) {
          const subVal = subField.value;
          if (subVal !== null && subVal !== undefined && subVal !== '') {
            const formatted = formatCellValue(subVal);
            if (formatted !== '' && formatted != null) {
              parts.push(String(formatted));
            }
          }
        }
      }
      if (parts.length > 0) return parts.join(', ');
      return '';
    }

    if (
      !('referenceType' in record) &&
      record.valueType !== 'manual' &&
      'entityReferences' in record &&
      record.entityReferences &&
      typeof record.entityReferences === 'object' &&
      !Array.isArray(record.entityReferences)
    ) {
      const refs = record.entityReferences as Record<
        string,
        { entityRecordName?: string; entityRecordId?: string }
      >;
      const parts = Object.values(refs)
        .map((r) =>
          r && typeof r === 'object'
            ? typeof r.entityRecordName === 'string' && r.entityRecordName
              ? r.entityRecordName
              : typeof r.entityRecordId === 'string'
                ? r.entityRecordId
                : ''
            : '',
        )
        .filter((s) => s.length > 0);
      if (parts.length > 0) return parts.join(', ');
      return '';
    }

    // GeoPoint objects - format as "lat, lng"
    if (isGeoPointValue(value)) {
      return `${value.latitude}, ${value.longitude}`;
    }

    // Reference objects - use just the path
    if (isReferenceValue(value)) {
      return value.path;
    }

    // URL objects
    if (value.url) return value.url;

    // Entity references
    if (value.entityRecordID) return value.displayValue || value.entityRecordID;

    if (
      'value' in record &&
      !('referenceType' in record) &&
      record.valueType !== 'manual'
    ) {
      return formatCellValue(record.value as unknown);
    }

    // Objects with displayValue string
    if (typeof value.displayValue === 'string') return value.displayValue;

    // Objects with formatted string (including empty — matches UI empty / "—" cells)
    if (typeof value.formatted === 'string') return value.formatted.trim();

    // Objects with label string
    if (typeof value.label === 'string') return value.label;

    if (Object.keys(record).length === 0) {
      return '';
    }

    // Default: serialize as JSON
    return JSON.stringify(value);
  }
  
  // Strings - return as-is
  if (typeof value === 'string') return value;
  
  // Fallback: convert to string
  return String(value);
};

/**
 * Exports table data to Excel with intelligent formatting and column handling
 * 
 * @param tableValue - Table data structure containing rows and metadata
 * @param columns - Column definitions including dataIndex and descriptions
 * @param options - Optional export configuration (form context, filename settings)
 * @returns Promise resolving to export result with success status and message
 * 
 * @remarks
 * Features:
 * - Automatic data type detection for proper Excel formatting
 * - Smart column width calculation based on content
 * - Range fields split into Start/End columns with timezone markers
 * - GeoPoint fields split into Latitude/Longitude columns
 * - Reference fields extract path property
 * - Row limit enforcement (max 50,000 rows)
 * - Excel sheet name truncation (31 character limit)
 * - Memory-safe blob URL handling
 * 
 * @example
 * ```typescript
 * const result = await exportTableToExcel(tableData, columns, {
 *   form: currentForm,
 *   formVersion: version,
 *   formType: type
 * });
 * if (result.success) {
 *   console.info(result.message); // "Exported 1,234 rows"
 * }
 * ```
 */

export {
  convertToClientTimeZone,
  convertTimestampToDate,
  formatCellValue,
  isRangeValue,
  isGeoPointValue,
  isReferenceValue,
  getDisplayLength,
};

export const exportTableToExcel = async (
  tableValue: TableValue,
  columns: TableColumnType[],
  options: ExportOptions = {}
): Promise<{ success: boolean; message: string; error?: Error }> => {
  try {
    if (!tableValue?.rows?.length) {
      return { success: false, message: 'No data to export' };
    }

    if (tableValue.rows.length > EXPORT_MAX_ROWS) {
      return {
        success: false,
        message: `Cannot export more than ${EXPORT_MAX_ROWS.toLocaleString()} rows`
      };
    }

    // Build schema with calculated widths and proper types
    // Use flatMap to allow range fields to expand into multiple columns
    const schema = columns
      .filter(col => col.dataIndex)
      .flatMap(col => {
        // Check what type of special column this is
        let isRangeColumn = false;
        let isGeoPointColumn = false;
        let isReferenceColumn = false;
        
        for (const row of tableValue.rows.slice(0, 5)) {
          const value = row.value?.[col.dataIndex];
          if (isRangeValue(value)) {
            isRangeColumn = true;
            break;
          } else if (isGeoPointValue(value)) {
            isGeoPointColumn = true;
            break;
          } else if (isReferenceValue(value)) {
            isReferenceColumn = true;
            break;
          }
        }
        
        if (isRangeColumn) {
          // Split range into Start and End columns
          const baseLabel = col.description?.shortLabel || col.dataIndex;
          
          const startColumn: any = {
            column: `Start ${baseLabel} (CT)`,
            type: Date, // Range fields typically contain timestamps/dates
            value: (row: TableRow) => {
              const rangeValue = row.value?.[col.dataIndex];
              if (isRangeValue(rangeValue)) {
                return convertTimestampToDate(rangeValue.start);
              }
              return '';
            },
            width: Math.max(EXPORT_COLUMN_MIN_WIDTH, `Start ${baseLabel} (CT)`.length + 2),
            format: 'MM/DD/YYYY HH:mm:ss',
            wrap: true
          };
          
          const endColumn: any = {
            column: `End ${baseLabel} (CT)`,
            type: Date,
            value: (row: TableRow) => {
              const rangeValue = row.value?.[col.dataIndex];
              if (isRangeValue(rangeValue)) {
                return convertTimestampToDate(rangeValue.end);
              }
              return '';
            },
            width: Math.max(EXPORT_COLUMN_MIN_WIDTH, `End ${baseLabel} (CT)`.length + 2),
            format: 'MM/DD/YYYY HH:mm:ss',
            wrap: true
          };
          
          return [startColumn, endColumn];
        } else if (isGeoPointColumn) {
          // Split GeoPoint into Latitude and Longitude columns
          const baseLabel = col.description?.shortLabel || col.dataIndex;
          
          const latitudeColumn: any = {
            column: `${baseLabel} Latitude`,
            type: Number,
            value: (row: TableRow) => {
              const geoValue = row.value?.[col.dataIndex];
              if (isGeoPointValue(geoValue)) {
                return geoValue.latitude;
              }
              return '';
            },
            width: Math.max(EXPORT_COLUMN_MIN_WIDTH, `${baseLabel} Latitude`.length + 2),
            wrap: true
          };
          
          const longitudeColumn: any = {
            column: `${baseLabel} Longitude`,
            type: Number,
            value: (row: TableRow) => {
              const geoValue = row.value?.[col.dataIndex];
              if (isGeoPointValue(geoValue)) {
                return geoValue.longitude;
              }
              return '';
            },
            width: Math.max(EXPORT_COLUMN_MIN_WIDTH, `${baseLabel} Longitude`.length + 2),
            wrap: true
          };
          
          return [latitudeColumn, longitudeColumn];
        } else if (isReferenceColumn) {
          // Convert Reference to just the path as a single column
          const baseLabel = col.description?.shortLabel || col.dataIndex;
          
          const referenceColumn: any = {
            column: baseLabel,
            type: String,
            value: (row: TableRow) => {
              const refValue = row.value?.[col.dataIndex];
              if (isReferenceValue(refValue)) {
                return refValue.path;
              }
              return '';
            },
            width: getColumnWidth(col, tableValue.rows, String),
            wrap: true
          };
          
          return [referenceColumn];
        } else {
          // Regular column processing
          const cellValue = (row: TableRow) => formatCellValue(row.value?.[col.dataIndex]);
          
          // Determine the appropriate type based on first non-null value
          let cellType: any = String;
          for (const row of tableValue.rows.slice(0, 10)) {
            const value = formatCellValue(row.value?.[col.dataIndex]);
            if (value !== '' && value != null) {
              if (value instanceof Date) {
                cellType = Date;
                break;
              } else if (typeof value === 'boolean') {
                cellType = Boolean;
                break;
              } else if (typeof value === 'number') {
                cellType = Number;
                break;
              }
            }
          }
          
          const schemaItem: any = {
            column: cellType === Date ? `${col.description?.shortLabel || col.dataIndex} (CT)` : col.description?.shortLabel || col.dataIndex,
            type: cellType,
            value: cellValue,
            width: getColumnWidth(col, tableValue.rows, cellType),
            wrap: true
          };
          
          // Add format for Date types
          if (cellType === Date) {
            schemaItem.format = 'MM/DD/YYYY HH:mm:ss';
          }
          
          return [schemaItem];
        }
      });

    if (!schema.length) {
      return { success: false, message: 'No visible columns to export' };
    }

    // Generate filename (without extension for sheet name)
    const filename = generateExportFilename(options);
    let sheetName = filename.replace('.xlsx', '');
    
    // Excel sheet names can't be longer than 31 characters
    if (sheetName.length > 31) {
      // Truncate but keep meaningful parts (prefer form number and version)
      const parts = sheetName.split('-');
      if (parts.length >= 4) {
        // Try to keep formNumber-version-formType format
        const formNumber = parts[0];
        const version = parts[1];
        const formType = parts[2];
        const shortName = `${formNumber}-${version}-${formType}`;
        sheetName = shortName.length <= 31 ? shortName : shortName.substring(0, 31);
      } else {
        // Fallback: just truncate
        sheetName = sheetName.substring(0, 31);
      }
    }
    
    const blob = await createExcelWorkbookBlobFromSchema({
      rows: tableValue.rows,
      schema,
      sheetName,
    });
    
    let url: string | null = null;
    try {
      url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
    } finally {
      // Always clean up the blob URL to prevent memory leaks
      if (url) {
        window.URL.revokeObjectURL(url);
      }
    }

    return {
      success: true,
      message: `Exported ${tableValue.rows.length.toLocaleString()} rows`
    };
  } catch (error) {
    console.error('Export failed:', error);
    return {
      success: false,
      message: 'Export failed',
      error: error as Error
    };
  }
};

/**
 * Generates an Excel Blob for email attachments without triggering a browser download.
 */
export const exportTableToExcelBlob = async (
  tableValue: TableValue,
  columns: TableColumnType[],
  options: ExportOptions = {}
): Promise<{ blob: Blob; filename: string; rowCount: number }> => {
  if (!tableValue?.rows?.length) {
    throw new Error('No data to export');
  }

  if (tableValue.rows.length > EXPORT_MAX_ROWS) {
    throw new Error(`Cannot export more than ${EXPORT_MAX_ROWS.toLocaleString()} rows`);
  }

  const schema = columns
    .filter(col => col.dataIndex)
    .flatMap(col => {
      let isRangeColumn = false;
      let isGeoPointColumn = false;
      let isReferenceColumn = false;

      for (const row of tableValue.rows.slice(0, 5)) {
        const value = row.value?.[col.dataIndex];
        if (isRangeValue(value)) {
          isRangeColumn = true;
          break;
        } else if (isGeoPointValue(value)) {
          isGeoPointColumn = true;
          break;
        } else if (isReferenceValue(value)) {
          isReferenceColumn = true;
          break;
        }
      }

      if (isRangeColumn) {
        const baseLabel = col.description?.shortLabel || col.dataIndex;
        const startColumn: any = {
          column: `Start ${baseLabel} (CT)`,
          type: Date,
          value: (row: TableRow) => {
            const rangeValue = row.value?.[col.dataIndex];
            if (isRangeValue(rangeValue)) {
              return convertTimestampToDate(rangeValue.start);
            }
            return '';
          },
          width: Math.max(EXPORT_COLUMN_MIN_WIDTH, `Start ${baseLabel} (CT)`.length + 2),
          format: 'MM/DD/YYYY HH:mm:ss',
          wrap: true
        };
        const endColumn: any = {
          column: `End ${baseLabel} (CT)`,
          type: Date,
          value: (row: TableRow) => {
            const rangeValue = row.value?.[col.dataIndex];
            if (isRangeValue(rangeValue)) {
              return convertTimestampToDate(rangeValue.end);
            }
            return '';
          },
          width: Math.max(EXPORT_COLUMN_MIN_WIDTH, `End ${baseLabel} (CT)`.length + 2),
          format: 'MM/DD/YYYY HH:mm:ss',
          wrap: true
        };
        return [startColumn, endColumn];
      } else if (isGeoPointColumn) {
        const baseLabel = col.description?.shortLabel || col.dataIndex;
        const latitudeColumn: any = {
          column: `${baseLabel} Latitude`,
          type: Number,
          value: (row: TableRow) => {
            const geoValue = row.value?.[col.dataIndex];
            if (isGeoPointValue(geoValue)) {
              return geoValue.latitude;
            }
            return '';
          },
          width: Math.max(EXPORT_COLUMN_MIN_WIDTH, `${baseLabel} Latitude`.length + 2),
          wrap: true
        };
        const longitudeColumn: any = {
          column: `${baseLabel} Longitude`,
          type: Number,
          value: (row: TableRow) => {
            const geoValue = row.value?.[col.dataIndex];
            if (isGeoPointValue(geoValue)) {
              return geoValue.longitude;
            }
            return '';
          },
          width: Math.max(EXPORT_COLUMN_MIN_WIDTH, `${baseLabel} Longitude`.length + 2),
          wrap: true
        };
        return [latitudeColumn, longitudeColumn];
      } else if (isReferenceColumn) {
        const baseLabel = col.description?.shortLabel || col.dataIndex;
        const referenceColumn: any = {
          column: baseLabel,
          type: String,
          value: (row: TableRow) => {
            const refValue = row.value?.[col.dataIndex];
            if (isReferenceValue(refValue)) {
              return refValue.path;
            }
            return '';
          },
          width: getColumnWidth(col, tableValue.rows, String),
          wrap: true
        };
        return [referenceColumn];
      } else {
        const cellValue = (row: TableRow) => formatCellValue(row.value?.[col.dataIndex]);
        let cellType: any = String;
        for (const row of tableValue.rows.slice(0, 10)) {
          const value = formatCellValue(row.value?.[col.dataIndex]);
          if (value !== '' && value != null) {
            if (value instanceof Date) {
              cellType = Date;
              break;
            } else if (typeof value === 'boolean') {
              cellType = Boolean;
              break;
            } else if (typeof value === 'number') {
              cellType = Number;
              break;
            }
          }
        }
        const schemaItem: any = {
          column: cellType === Date ? `${col.description?.shortLabel || col.dataIndex} (CT)` : col.description?.shortLabel || col.dataIndex,
          type: cellType,
          value: cellValue,
          width: getColumnWidth(col, tableValue.rows, cellType),
          wrap: true
        };
        if (cellType === Date) {
          schemaItem.format = 'MM/DD/YYYY HH:mm:ss';
        }
        return [schemaItem];
      }
    });

  if (!schema.length) {
    throw new Error('No visible columns to export');
  }

  const filename = generateExportFilename(options);
  let sheetName = filename.replace('.xlsx', '');
  if (sheetName.length > 31) {
    const parts = sheetName.split('-');
    if (parts.length >= 4) {
      const formNumber = parts[0];
      const version = parts[1];
      const formType = parts[2];
      const shortName = `${formNumber}-${version}-${formType}`;
      sheetName = shortName.length <= 31 ? shortName : shortName.substring(0, 31);
    } else {
      sheetName = sheetName.substring(0, 31);
    }
  }

  const blob = await createExcelWorkbookBlobFromSchema({
    rows: tableValue.rows,
    schema,
    sheetName,
  });

  return { blob, filename, rowCount: tableValue.rows.length };
};

type SchemaItem = {
  column: string;
  type: typeof String | typeof Number | typeof Boolean | typeof Date;
  value: (row: TableRow) => unknown;
  width: number;
  wrap?: boolean;
  format?: string;
};

async function createExcelWorkbookBlobFromSchema(params: {
  rows: readonly TableRow[];
  schema: readonly SchemaItem[];
  sheetName: string;
}): Promise<Blob> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet(sanitizeExcelSheetName(params.sheetName) || 'Sheet1');

  worksheet.addRow(params.schema.map((col) => col.column));

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { wrapText: true, vertical: 'top' };

  for (let i = 0; i < params.schema.length; i++) {
    const schemaItem = params.schema[i];
    const column = worksheet.getColumn(i + 1);
    column.width = schemaItem.width;
    column.alignment = { wrapText: schemaItem.wrap !== false, vertical: 'top' };
    if (schemaItem.type === Date) {
      column.numFmt = schemaItem.format ?? 'MM/DD/YYYY HH:mm:ss';
    }
  }

  for (const row of params.rows) {
    const values = params.schema.map((schemaItem) => {
      const value = schemaItem.value(row);
      if (schemaItem.type === Date) {
        if (value === '' || value === null || value === undefined) return null;
        return value instanceof Date ? value : null;
      }
      if (schemaItem.type === Number) {
        if (value === '' || value === null || value === undefined) return null;
        return typeof value === 'number' ? value : null;
      }
      if (schemaItem.type === Boolean) {
        if (value === '' || value === null || value === undefined) return null;
        return typeof value === 'boolean' ? value : null;
      }
      return value === null || value === undefined ? '' : (value as any);
    });

    worksheet.addRow(values);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer as ArrayBuffer], { type: EXCEL_MIME_TYPE });
}
